import * as React from 'react'
import { DBModels } from '../state/db_models'
import { NevoLevel } from '../state/nevo'
import { KewOption, Layer, ShapeLayer } from '../state'
import { iconForLayerType } from "../utils/util"
import { AnnotationRecord, CompiledDatasetRecord, getAnnotations, TeamDataRecord } from '../state/saved_dataset'
import { designations } from '../modelling/designations'
import { IMDProperties } from '../reify_layer/imd'
import { ProjectPermissions } from './project_editor'
import { KewPointOptions, KewTreeCrownOptions } from '../reify_layer/kew'
import { seasonYearOptions } from '../modelling/components/kew_samples_component'
import { natmap_outputs } from '../modelling/components/natmap_soil_component'
import { highlightSearchMatch } from './search_highlight'
import { geoserverValueColorsFromEntries } from '../layers/geoserver_categorical'
import { isRegionAvailable } from "../utils/region_permissions"
import { isMapLayerPubliclyAvailable, usePublicShareSettings } from "./public_share_settings"

interface AddLayerButtonProps {
  prototype: Layer
  addLayer: (Layer) => void
  searchQuery?: string
}
const AddLayerButton = ({ prototype, addLayer, searchQuery = "" }: AddLayerButtonProps) => (
  <div
    className="px-3 py-2 d-flex align-items-center add-layer-button"
    onClick={() => addLayer(prototype)}
  >
    <i className={`fas fa-fw ${iconForLayerType(prototype.type)}`} />
    <div className="ml-2 mr-3 flex-grow-1 add-layer-button-name">
      {searchQuery.trim()
        ? highlightSearchMatch(prototype.name, searchQuery)
        : prototype.name}
    </div>
    <i className="fas fa-plus add-layer-button-plus" />
  </div>
)

interface SectionProps {
  title: string
  children?: React.ReactNode
  forceOpen?: boolean
  searchQuery?: string
}
const Section = ({ title, children, forceOpen, searchQuery = "" }: SectionProps) => (
  <details open={forceOpen || undefined}>
    <summary className="px-3 py-2 bg-light border-bottom">
      {searchQuery.trim() ? highlightSearchMatch(title, searchQuery) : title}
    </summary>
    <div className="d-flex flex-column border-bottom bg-white">
      {children}
    </div>
  </details>
)

interface PaletteItem {
  key: string
  layer: Layer
}

interface PaletteSectionData {
  title: string
  items: PaletteItem[]
  /** Region scope tag — e.g. "UK", "Indonesia". Filtered via `isRegionAvailable`. */
  region?: string
}

function filterPaletteSections(sections: PaletteSectionData[], searchQuery: string): PaletteSectionData[] {
  const query = searchQuery.trim().toLowerCase()
  if (!query) return sections

  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter(({ layer }) =>
        `${section.title} ${layer.name}`.toLowerCase().includes(query)
      ),
    }))
    .filter((section) => section.items.length > 0)
}

interface LayerPaletteProps {
  addLayer: (Layer) => void
  hide: () => void
  dbModels: DBModels
  getTeamDatasets: () => Promise<Array<CompiledDatasetRecord>>
  getTeamDataLayers: () => Promise<Array<TeamDataRecord>>
  /** Used to load Paintbrush (annotation) layers for the team section. */
  teamId: number
  teamName: string
  permissions: ProjectPermissions
}

const TEAM_DATA_VECTOR_COLORS: ShapeLayer["colors"] = {
  stroke: [0, 153, 255, 0],
  fill: [0, 153, 255, 0.15],
  strokeWidth: 0
}

function getWorkspaceName(geoserverLayerId?: string | null): string | undefined {
  const firstLayerId = geoserverLayerId?.split(',').map(part => part.trim()).find(Boolean)
  if (!firstLayerId || !firstLayerId.includes(':')) return undefined
  return firstLayerId.split(':', 2)[0]
}

function buildTeamDataLayerPrototypes(teamData: TeamDataRecord): Layer[] {
  const displayName = teamData.display_name || teamData.name || `Data ${teamData.id}`
  const publishedLayerIds = teamData.geoserver_layer_id?.split(',').map(part => part.trim()).filter(Boolean) || []
  const workspace = getWorkspaceName(teamData.geoserver_layer_id)

  if (teamData.vector_layers?.length && workspace) {
    return teamData.vector_layers.map((vectorLayer) => {
      const label = teamData.vector_layers && teamData.vector_layers.length > 1
        ? `${displayName}: ${vectorLayer.layer_name}`
        : displayName

      return {
        type: "ShapeLayer",
        name: label,
        identifier: `${workspace}:${vectorLayer.layer_name}`,
        colors: TEAM_DATA_VECTOR_COLORS,
        fill: "greyscale",
        externalLink: teamData.geoserver_url || undefined,
        vectorFields: vectorLayer.fields,
        fieldValues: vectorLayer.field_values,
        selectedField: undefined,
        visible: true,
        opacity: 1
      }
    })
  }

  return publishedLayerIds.map((layerName, index) => {
    const hasAttachedComponent = teamData.category_labels !== undefined
    const categoryLabels = hasAttachedComponent
      ? (teamData.category_labels ?? []).filter(
          entry => entry.label.trim().length > 0 && entry.value !== undefined && entry.value !== null,
        )
      : undefined
    const uniqueValues = hasAttachedComponent
      ? (categoryLabels ?? []).map(entry => entry.value)
      : (teamData.unique_values || [])
    const valueColors = categoryLabels
      ? geoserverValueColorsFromEntries(categoryLabels)
      : undefined

    return {
    type: "GeoserverLayer",
    layerName,
    name: publishedLayerIds.length > 1 ? `${displayName}: ${layerName.split(':').pop()}` : displayName,
    visible: true,
    opacity: 1,
    fill: teamData.categorical_raster ? "rainbow-soft" : "greyscale",
    categoricalRaster: !!teamData.categorical_raster,
    categoricalValueCount: teamData.categorical_value_count ?? undefined,
    rasterMinValue: teamData.raster_min_value ?? undefined,
    rasterMaxValue: teamData.raster_max_value ?? undefined,
    valueColors,
    uniqueValues,
    categoryLabels,
    externalLink: teamData.geoserver_url ? `${teamData.geoserver_url}/wms` : undefined
  }
  })
}

function buildPaintbrushLayerPrototype(ann: AnnotationRecord): Layer {
  const legendClasses =
    ann.classes && ann.classes.length > 0
      ? [...ann.classes]
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((c) => ({ className: c.name, color: c.color }))
      : undefined
  return {
    type: "PaintbrushGeoJSONLayer",
    name: ann.name?.trim() ? ann.name : `Paintbrush #${ann.id}`,
    annotationId: ann.id,
    geojsonUrl: ann.geojson_url!,
    visible: true,
    opacity: 0.9,
    legendClasses,
  }
}

export function buildLayerPaletteSections({
  teamDatasets,
  teamDataLayers,
  paintbrushAnnotations,
  teamName,
  permissions,
  dbModels,
}: {
  teamDatasets: CompiledDatasetRecord[]
  teamDataLayers: TeamDataRecord[]
  paintbrushAnnotations: AnnotationRecord[]
  teamName: string
  permissions: ProjectPermissions
  dbModels: DBModels
}): PaletteSectionData[] {
  const sections: PaletteSectionData[] = []

  if (teamDatasets.length > 0 || teamDataLayers.length > 0 || paintbrushAnnotations.length > 0) {
    const items: PaletteItem[] = [
      ...teamDatasets
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((dataset) => ({
          key: `dataset-${dataset.id}`,
          layer: {
            type: "DatasetLayer" as const,
            id: dataset.id,
            name: dataset.name,
            visible: true,
            opacity: 1,
            fill: "greyscale" as const,
            colors: [],
            overrideBounds: false,
            bounds: undefined
          },
        })),
      ...teamDataLayers
        .slice()
        .sort((a, b) => a.display_name.localeCompare(b.display_name))
        .flatMap((teamData) =>
          buildTeamDataLayerPrototypes(teamData).map((prototype, index) => ({
            key: `${teamData.id}-${prototype.type}-${index}`,
            layer: prototype,
          }))
        ),
      ...paintbrushAnnotations
        .slice()
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        .map((ann) => ({
          key: `paintbrush-${ann.id}`,
          layer: buildPaintbrushLayerPrototype(ann),
        })),
    ]
    sections.push({ title: teamName, items })
  }

  if (isRegionAvailable("Indonesia", permissions)) {
    sections.push({
      title: "Gorontalo",
      region: "Indonesia",
      items: [{
        key: "indonesia-biodiversity-observations",
        layer: {
          type: "ShapeLayer",
          name: "Biodiversity observations",
          identifier: "ind_team:biodiversity_observations",
          colors: {
            stroke: [255, 255, 255, 1],
            fill: [46, 139, 87, 1],
            strokeWidth: 1.5
          },
          fill: "rainbow-soft",
          fieldValues: {
            species_class: ["Bird", "Crustacean", "Fern", "Fish", "Grass", "Herb", "Mammal", "Reptile", "Shrub", "Tree"],
          },
          valueColors: {
            species_class: [
              [78, 163, 224, 1],   // Bird — sky
              [224, 112, 64, 1],   // Crustacean — crab orange
              [60, 179, 113, 1],   // Fern — cool green
              [26, 155, 181, 1],   // Fish — water cyan
              [196, 214, 74, 1],   // Grass — lime
              [139, 195, 74, 1],   // Herb — leaf green
              [160, 103, 58, 1],   // Mammal — brown
              [196, 160, 53, 1],   // Reptile — ochre
              [107, 142, 35, 1],   // Shrub — olive
              [27, 107, 58, 1],    // Tree — forest
            ],
          },
          selectedField: "species_class",
          visible: true,
          opacity: 1
        },
      }],
    })
  }

  if (permissions.KewSamples) {
    sections.push({
      title: "Kew",
      items: [
        {
          key: "kew-wakehurst-soil",
          layer: {
            type: "KewPointLayer",
            name: "Wakehurst Soil",
            identifier: "kew:wakehurst_soil_rp3857_v2",
            fill: "hsv",
            metric: KewPointOptions.indexOf(KewPointOptions.find(option => option.value === "ph")!),
            visible: true,
            opacity: 1,
            seasonYear: seasonYearOptions[0]
          },
        },
        {
          key: "kew-tree-crowns-deciduous",
          layer: {
            type: "KewShapeLayer",
            name: "Wakehurst Tree Crowns (Deciduous)",
            identifier: "kew:bl_crown_3857",
            visible: true,
            opacity: 1,
            color: { stroke: [160,82,45, 1], fill: [160,82,45, 1] },
            fill: "jet",
            metric: KewTreeCrownOptions[0]
          },
        },
        {
          key: "kew-tree-crowns-coniferous",
          layer: {
            type: "KewShapeLayer",
            name: "Wakehurst Tree Crowns (Coniferous)",
            identifier: "kew:conifer_crown_3857",
            visible: true,
            opacity: 1,
            color: { stroke: [34,139,34, 1], fill: [34,139,34, 1] },
            fill: "jet",
            metric: KewTreeCrownOptions[0]
          },
        },
      ],
    })
  }

  sections.push({
    title: "Ancient Tree Inventory",
    items: [{
      key: "ati",
      layer: {
        type: "AtiLayer",
        name: "Ancient Tree Inventory",
        visible: true,
        opacity: 1,
        colors: {
          ancient: [255, 183, 0, 1],
          veteran: [0, 82, 11, 1],
          notable: [158, 52, 235, 1],
          public: [0, 217, 255, 1],
          private: [235, 0, 0, 1],
        }
      },
    }],
  })

  sections.push({
    title: "Designations",
    items: designations
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(({ name, identifier, stroke, fill, attribution }) => ({
        key: `designation-${identifier}`,
        layer: {
          type: "ShapeLayer" as const,
          name,
          colors: { stroke, fill },
          identifier,
          visible: true,
          opacity: 1,
          attribution
        },
      })),
  })

  sections.push({
    title: "OS Boundaries",
    items: [
      {
        name: "Historic Counties",
        identifier: "shapefiles:boundary_line_historic_counties",
        target: "all"
      },
      {
        name: "Ceremonial Counties",
        identifier: "shapefiles:boundary_line_ceremonial_counties",
        target: "all"
      },
      {
        name: "Westminster Constituencies (2010-2024)",
        identifier: "shapefiles:westminster_const ",
        target: "all"
      },
      {
        name: "Westminster Constituencies (2024 onwards)",
        identifier: "shapefiles:bdline_gb__westminster_const",
        target: "all"
      },
      {
        name: "Polling Districts",
        identifier: "shapefiles:polling_districts_england",
        target: "all"
      },
      {
        name: "District Councils",
        identifier: "shapefiles:district_borough_unitary",
        target: "all"
      },
      {
        name: "Wealden District Council",
        identifier: "shapefiles:district_borough_unitary",
        target: "Wealden District"
      }
    ]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(({ name, identifier, target }) => ({
        key: `os-boundary-${identifier}-${target}`,
        layer: {
          type: "BoundaryLayer" as const,
          name,
          identifier,
          visible: true,
          opacity: 1,
          target
        },
      })),
  })

  sections.push({
    title: "NEVO",
    items: [
      { name: "NEVO data (2km grid)", level: "2km" as const },
      { name: "NEVO data (Subcatchments)", level: "subbasins" as const },
      { name: "NEVO data (National Parks)", level: "national_parks" as const },
      { name: "NEVO data (Local Authorities)", level: "lad" as const },
      { name: "NEVO data (Catchments)", level: "basins" as const },
      { name: "NEVO data (Counties)", level: "counties_uas" as const },
      { name: "NEVO data (GORs)", level: "regions" as const },
      { name: "NEVO data (Countries)", level: "countries" as const },
    ].map(({ name, level }) => ({
      key: `nevo-${level}`,
      layer: {
        type: "NevoLayer" as const,
        name,
        visible: true,
        opacity: 1,
        level,
        property: "urban_ha",
        fill: "heatmap" as const
      },
    })),
  })

  sections.push({
    title: "ORVal",
    items: [
      { name: "Parks", source: "ORVAL:parks_england", style: "ORVAL:orvalparks" },
      { name: "Beaches", source: "ORVAL:beaches_england", style: "" },
      { name: "Paths", source: "ORVAL:paths_england", style: "" },
      { name: "Path Access Points", source: "ORVAL:paths_england_accesspts", style: "" },
    ].map(({ name, source, style }) => ({
      key: `orval-${source}-${name}`,
      layer: {
        type: "ORValLayer" as const,
        name,
        visible: true,
        opacity: 1,
        source,
        style
      },
    })),
  })

  sections.push({
    title: "Indices of Multiple Deprivation",
    items: [{ year: 2019 }].map(({ year }) => ({
      key: `imd-${year}`,
      layer: {
        type: "IMDLayer" as const,
        name: `Indices of Multiple Deprivation ${year}`,
        visible: true,
        opacity: 1,
        fill: "jet" as const,
        property: IMDProperties[0],
        year
      },
    })),
  })

  sections.push({
    title: "UKCEH Land Cover Maps",
    items: [
      {
        key: "ukceh-land-cover-2021",
        layer: {
          type: "CehLandCoverLayer" as const,
          name: "UKCEH Land Cover Map 2021",
          visible: true,
          opacity: 1,
          year: 2021,
        },
      },
      {
        key: "ukceh-woody-linear",
        layer: {
          type: "ShapeLayer" as const,
          name: "UKCEH Woody Linear Features",
          identifier: "ukceh:linearwoody2",
          colors: {
            stroke: [0, 255, 0, 1],
            fill: [0, 255, 0, 1],
            strokeWidth: 3
          },
          visible: true,
          opacity: 1,
          minZoom: 15
        },
      },
    ],
  })

  sections.push({
    title: "Crop Map of England",
    items: [{ year: 2018 }, { year: 2019 }, { year: 2020 }, { year: 2021 }].map(({ year }) => ({
      key: `crome-${year}`,
      layer: {
        type: "CropMapLayer" as const,
        name: `Crop Map of England (CROME) ${year}`,
        visible: true,
        opacity: 1,
        year
      },
    })),
  })

  if (permissions.NATMAPSoil) {
    sections.push({
      title: "Natmap Soil",
      items: [{
        key: "natmap-soil-carbon",
        layer: {
          type: "WFSLayer",
          name: "Natmap Soil Carbon",
          layer: "cranfield_soil:NATMAPcarbon",
          attribution: "Cranfield University",
          propIdx: 0,
          visible: true,
          opacity: 1,
          fill: "jet"
        },
      }],
    })
  }

  if (dbModels.overlays.length > 0) {
    sections.push({
      title: "Overlays",
      items: dbModels.overlays.map(layer => ({
        key: `overlay-${layer.id}`,
        layer: {
          type: "OverlayLayer" as const,
          name: layer.name,
          visible: true,
          opacity: 1,
          id: layer.id,
          strokeWidth: 2,
          fillOpacity: 0.2
        },
      })),
    })
  }

  const aerialItems: PaletteItem[] = []
  if (permissions.indonesiaEnabled) {
    aerialItems.push({
      key: "aerial-indonesia-sentinel",
      layer: {
        layerName: "ind:merged_visual_2025-05-01_2025-05-31",
        type: "GeoserverLayer",
        name: "Sentinel-2: Gorontalo (May 2025)",
        visible: true,
        opacity: 1,
      },
    })
  }
  if (permissions.PlanetData) {
    aerialItems.push({
      key: "aerial-planet-gorontalo-villages-2026",
      layer: {
        type: "WcsRgbLayer",
        name: "Planet - 2026 - Gorontalo",
        coverageId: "ind:2026_March_geoserver",
        // Slightly inset from native bounds to avoid GeoServer edge NPEs on full-extent requests.
        nativeExtent: [502400, 70800, 509500, 79900],
        nativeCrs: "EPSG:32651",
        // Planet AnalyticMS: B1=Blue, B2=Green, B3=Red, B4=NIR
        rgbBands: [3, 2, 1],
        stretchMax: 2000,
        scaleFactor: 0.35,
        visible: true,
        opacity: 1,
      },
    })
    aerialItems.push({
      key: "aerial-planet-gorontalo-villages-2019",
      layer: {
        type: "WcsRgbLayer",
        name: "Planet - 2019 - Gorontalo",
        coverageId: "ind:2019_merged_3villages",
        nativeExtent: [502400, 70800, 509500, 79900],
        nativeCrs: "EPSG:32651",
        rgbBands: [3, 2, 1],
        stretchMax: 2000,
        scaleFactor: 0.35,
        visible: true,
        opacity: 1,
      },
    })
  }
  if (permissions.KewRgb25cm) {
    aerialItems.push({
      key: "aerial-rgb-25cm",
      layer: {
        layerName: "rgb:full_mosaic_3857",
        type: "GeoserverLayer",
        name: "RGB 25cm",
        visible: true,
        opacity: 1,
      },
    })
  }
  aerialItems.push(
    {
      key: "aerial-s2cloudless-2016",
      layer: {
        layerName: "s2cloudless_3857",
        type: "GeoserverLayer",
        name: "Cloudless Sentinel-2 2016 (Provided by EOX)",
        visible: true,
        opacity: 1,
        externalLink: "https://tiles.maps.eox.at/wms",
        attribution: '&copy; <a href="https://eox.at/">EOX</a>'
      },
    },
    {
      key: "aerial-s2cloudless-2024",
      layer: {
        layerName: "s2cloudless-2024_3857",
        type: "GeoserverLayer",
        name: "Cloudless Sentinel-2 2024 (Provided by EOX)",
        visible: true,
        opacity: 1,
        externalLink: "https://tiles.maps.eox.at/wms",
        attribution: '&copy; <a href="https://eox.at/">EOX</a>'
      },
    },
    ...dbModels.mapTileLayers.map(layer => ({
      key: `map-tile-${layer.id}`,
      layer: {
        type: "MapTileLayer" as const,
        name: layer.name,
        visible: true,
        opacity: 1,
        id: layer.id
      },
    }))
  )
  sections.push({ title: "Aerial/Satellite imagery", items: aerialItems })

  sections.push({
    title: "Base layers",
    items: [{
      key: "osm",
      layer: {
        type: "OsmLayer",
        name: "OpenStreetMap",
        visible: true,
        opacity: 1
      },
    }],
  })

  return sections
}

/** Stable identity for a palette prototype or a layer already on the map. */
export function layerCatalogKey(layer: Layer): string {
  switch (layer.type) {
    case "DatasetLayer":
      return `dataset:${layer.id}`
    case "ShapeLayer":
      return `shape:${layer.identifier}`
    case "BoundaryLayer":
      return `boundary:${layer.identifier}:${layer.target}`
    case "GeoserverLayer":
      return `geoserver:${layer.layerName}:${layer.externalLink ?? ""}`
    case "KewPointLayer":
      return `kew-point:${layer.identifier}`
    case "KewShapeLayer":
      return `kew-shape:${layer.identifier}`
    case "KewLayer":
      return `kew:${layer.location}:${layer.metric}`
    case "AtiLayer":
      return "ati"
    case "NevoLayer":
      return `nevo:${layer.level}`
    case "ORValLayer":
      return `orval:${layer.source}:${layer.name}`
    case "IMDLayer":
      return `imd:${layer.year}`
    case "CehLandCoverLayer":
      return `ceh:${layer.year}`
    case "CropMapLayer":
      return `crome:${layer.year}`
    case "WFSLayer":
      return `wfs:${layer.layer}`
    case "OverlayLayer":
      return `overlay:${layer.id}`
    case "WcsRgbLayer":
      return `wcs:${layer.coverageId}`
    case "MapTileLayer":
      return `map-tile:${layer.id}`
    case "OsmLayer":
      return "osm"
    case "PaintbrushGeoJSONLayer":
      return `paintbrush:${layer.annotationId}`
    case "ModelOutputLayer":
      return `model-output:${layer.nodeId}`
  }
}

export const LayerPalette = ({ addLayer, hide, dbModels, getTeamDatasets, getTeamDataLayers, teamId, teamName, permissions }: LayerPaletteProps) => {

  const [teamDatasets, setTeamDatasets] = React.useState<CompiledDatasetRecord[]>([])
  const [teamDataLayers, setTeamDataLayers] = React.useState<TeamDataRecord[]>([])
  const [paintbrushAnnotations, setPaintbrushAnnotations] = React.useState<AnnotationRecord[]>([])
  const [searchQuery, setSearchQuery] = React.useState("")
  const publicShareSettings = usePublicShareSettings()

  React.useEffect(() => {
    getTeamDatasets()
      .then((datasets) => {
        setTeamDatasets(datasets)
      })
      .catch((error) => {
        console.error('Error fetching team datasets:', error)
      })

    getTeamDataLayers()
      .then((teamData) => {
        setTeamDataLayers(teamData.filter((datum) => datum.publish_status === 'published' && !!datum.geoserver_layer_id))
      })
      .catch((error) => {
        console.error('Error fetching team data:', error)
      })

    getAnnotations(teamId)
      .then((annotations) => {
        setPaintbrushAnnotations(annotations.filter((a) => !!a.geojson_url))
      })
      .catch((error) => {
        console.error('Error fetching Paintbrush annotations:', error)
      })
  }, [teamId])

  const paletteSections = React.useMemo(
    () => buildLayerPaletteSections({
      teamDatasets,
      teamDataLayers,
      paintbrushAnnotations,
      teamName,
      permissions,
      dbModels,
    }),
    [teamDatasets, teamDataLayers, paintbrushAnnotations, teamName, permissions, dbModels]
  )

  const filteredSections = React.useMemo(
    () => filterPaletteSections(paletteSections, searchQuery)
      .filter(section => isRegionAvailable(section.region, permissions))
      .map(section => ({
        ...section,
        items: section.items.filter(item => isMapLayerPubliclyAvailable(item.key, publicShareSettings)),
      }))
      .filter(section => section.items.length > 0),
    [paletteSections, searchQuery, permissions, publicShareSettings]
  )
  const isSearching = searchQuery.trim().length > 0
  const totalLayers = publicShareSettings
    ? paletteSections
      .filter(section => isRegionAvailable(section.region, permissions))
      .reduce((sum, section) => sum + section.items.filter(item => isMapLayerPubliclyAvailable(item.key, publicShareSettings)).length, 0)
    : paletteSections.reduce((sum, section) => sum + section.items.length, 0)
  const matchCount = filteredSections.reduce((sum, section) => sum + section.items.length, 0)

  return (
    <div className={`bg-light border-right d-flex flex-column layer-palette${publicShareSettings ? " layer-palette--public" : ""}`}>
      <div className="px-3 py-2 border-top border-bottom d-flex align-items-center justify-content-between">
        Add layer
        <i className="fas fa-times" style={{ cursor: "pointer" }} onClick={hide} />
      </div>
      <div className="layer-palette-search">
        <i className="fas fa-magnifying-glass" aria-hidden />
        <input
          type="search"
          placeholder="Search layers…"
          aria-label="Search layers"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
        />
        {searchQuery && (
          <button
            type="button"
            className="layer-palette-search-clear"
            onClick={() => setSearchQuery("")}
            aria-label="Clear search"
            title="Clear search"
          >
            <i className="fas fa-xmark" />
          </button>
        )}
      </div>
      <div className="layer-palette-summary px-3 py-1 border-bottom bg-light text-muted small">
        {isSearching
          ? matchCount === 0
            ? `No layers match “${searchQuery.trim()}”`
            : `${matchCount} match${matchCount === 1 ? "" : "es"}`
          : `${totalLayers} layer${totalLayers === 1 ? "" : "s"}`}
      </div>
      <div className="flex-grow-1" style={{ overflowY: "auto", flexBasis: "0px" }}>
        {isSearching && matchCount === 0 ? (
          <div className="px-3 py-4 text-muted text-center small">
            Try a different search term or browse categories below once you clear the search.
          </div>
        ) : (
          filteredSections.map((section) => (
            <Section
              key={section.title}
              title={section.title}
              forceOpen={isSearching}
              searchQuery={searchQuery}
            >
              {section.items.map(({ key, layer }) => (
                <AddLayerButton
                  key={key}
                  addLayer={addLayer}
                  prototype={layer}
                  searchQuery={searchQuery}
                />
              ))}
            </Section>
          ))
        )}
      </div>
    </div>
  )
}

